import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ArrowLeft, Check, Copy, Key, ShieldCheck, X, ChartBar, DownloadSimple, Trash, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import InsightsPanel from "@/components/admin/InsightsPanel";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "insights", label: "Insights" },
];

// Friendly copy + destructive intent for each admin action that needs confirmation.
// Surfaced via a single shared AlertDialog (replacing window.confirm).
const CONFIRM_COPY = {
  revoke: {
    title: "Revoke this user's access?",
    description: "They'll be signed out on their next request and won't be able to sign back in until you re-approve them.",
    cta: "Revoke access",
    destructive: true,
  },
  promote: {
    title: "Make this user an admin?",
    description: "They'll be able to approve users, issue sign-in codes, and promote or demote other admins.",
    cta: "Make admin",
    destructive: false,
  },
  demote: {
    title: "Remove admin from this user?",
    description: "They'll lose admin permissions but keep their beta access.",
    cta: "Remove admin",
    destructive: true,
  },
  delete: {
    title: "Delete user and purge their data?",
    description:
      "This permanently removes the user, their solo households, all projects + photos in those households, their waitlist record, OTP codes, future requests and safety events. Households shared with other members are preserved; ownership transfers to the oldest remaining member.",
    cta: "Delete user",
    destructive: true,
  },
};

export default function AdminPage() {
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codeModal, setCodeModal] = useState(null); // { email, code, expires_in_minutes }
  const [issuing, setIssuing] = useState(null);     // user_id currently issuing
  // Single shared confirm dialog: { action: "revoke"|"promote"|"demote"|"delete", user }
  const [pendingConfirm, setPendingConfirm] = useState(null);
  // Admin User Detail drawer state (full user record + destructive actions).
  const [detailUser, setDetailUser] = useState(null);          // selected user row (lite payload)
  const [detail, setDetail] = useState(null);                  // /detail response
  const [detailLoading, setDetailLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const { user: me } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    if (tab === "insights") {
      setLoading(false);
      setUsers([]);
      return;
    }
    try {
      setLoading(true);
      const r = await api.get("/admin/users", { params: { status: tab } });
      setUsers(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admins only");
        navigate("/", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  const approve = async (uid) => {
    await api.post(`/admin/users/${uid}/approve`);
    toast.success("Approved");
    load();
  };
  const reject = async (uid) => {
    await api.post(`/admin/users/${uid}/reject`);
    toast.success("Rejected");
    load();
  };
  const revoke = async (uid) => {
    await api.post(`/admin/users/${uid}/revoke`);
    toast.success("Revoked");
    load();
  };

  const promote = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/promote`);
      toast.success("Now an admin");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote");
    }
  };
  const demote = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/demote`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove admin");
    }
  };

  // Single entry-point used by every destructive button — opens the shared AlertDialog.
  const askConfirm = (action, user) => setPendingConfirm({ action, user });

  const runConfirm = async () => {
    if (!pendingConfirm) return;
    const { action, user } = pendingConfirm;
    setPendingConfirm(null);
    if (action === "revoke") return revoke(user.id);
    if (action === "promote") return promote(user);
    if (action === "demote") return demote(user);
    if (action === "delete") return purgeUser(user);
  };

  const openDetail = async (u) => {
    setDetailUser(u);
    setDetail(null);
    setDetailLoading(true);
    try {
      // Pull both the admin detail (cascade-context) AND the drilldown
      // (projects + categories + ai usage) in parallel.
      const [d, drill] = await Promise.all([
        api.get(`/admin/users/${u.id}/detail`).then((r) => r.data),
        api.get(`/admin/insights/users/${u.id}`).then((r) => r.data).catch(() => null),
      ]);
      setDetail({ ...d, drilldown: drill });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load user");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailUser(null);
    setDetail(null);
  };

  const exportUser = async (u) => {
    try {
      const r = await api.get(`/admin/users/${u.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (u.email || u.id).replace(/[^a-z0-9_.-]+/gi, "_");
      a.download = `halfjob-user-${safe}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("User data exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't export user");
    }
  };

  const purgeUser = async (u) => {
    try {
      setPurging(true);
      const r = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      // Quick summary toast — gives the admin confidence the cascade ran.
      const s = r.data || {};
      const bits = [];
      if (s.households_deleted) bits.push(`${s.households_deleted} household${s.households_deleted > 1 ? "s" : ""} removed`);
      if (s.households_transferred) bits.push(`${s.households_transferred} transferred`);
      if (s.households_left) bits.push(`left ${s.households_left}`);
      if (s.projects_deleted) bits.push(`${s.projects_deleted} project${s.projects_deleted > 1 ? "s" : ""} removed`);
      if (s.photos_removed) bits.push(`${s.photos_removed} photo${s.photos_removed > 1 ? "s" : ""}`);
      if (bits.length) toast.message(bits.join(" · "));
      closeDetail();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete user");
    } finally {
      setPurging(false);
    }
  };

  const issueCode = async (u) => {
    try {
      setIssuing(u.id);
      const r = await api.post(`/admin/users/${u.id}/issue-login-code`);
      const payload = { ...r.data, name: u.name, user_email: u.email };
      setCodeModal(payload);
      // Auto-copy on issue so the admin can paste straight into Signal/SMS
      // without clicking. Toast nudges them that it's already on the clipboard.
      try {
        await navigator.clipboard.writeText(payload.code);
        toast.success("Code copied to clipboard");
      } catch (_) { /* clipboard may be unavailable on http preview */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue code");
    } finally {
      setIssuing(null);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };

  // Public host the user logs in from — derived from REACT_APP_PUBLIC_URL
  // (set at deploy time) and falls back to the current origin so the modal
  // produces the right instructions in both preview and production.
  const loginHost = () => {
    const cfg = process.env.REACT_APP_PUBLIC_URL;
    if (cfg) return cfg.replace(/\/+$/, "");
    if (typeof window !== "undefined") return window.location.origin;
    return "https://app.halfjob.com.au";
  };

  const adminCodeEmailBody = (m) =>
    `Hi${m.name ? " " + m.name : ""},\n\n` +
    `Here's your one-time Half Job sign-in code:\n\n` +
    `${m.code}\n\n` +
    `To sign in:\n` +
    `  1. Visit ${loginHost()}\n` +
    `  2. Tap "Have a code already?"\n` +
    `  3. Enter your email (${m.user_email}) and the code above\n\n` +
    `Or use this direct link: ${loginHost()}/code?email=${encodeURIComponent(m.user_email)}\n\n` +
    `The code expires in ${m.expires_in_minutes} minutes and can only be used once.\n\n` +
    `— Half Job`;

  const copyInstructions = (m) => {
    const txt =
      `Half Job sign-in code: ${m.code}\n\n` +
      `1. Go to ${loginHost()}\n` +
      `2. Tap "Have a code already?"\n` +
      `3. Enter ${m.user_email} and ${m.code}\n\n` +
      `Direct link: ${loginHost()}/code?email=${encodeURIComponent(m.user_email)}\n` +
      `Expires in ${m.expires_in_minutes} min.`;
    navigator.clipboard.writeText(txt);
    toast.success("Instructions copied — paste them into SMS/Signal/email");
  };

  return (
    <div className="min-h-screen pb-32" data-testid="admin-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="admin-back">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-2 mt-3">
          <ShieldCheck size={28} weight="duotone" className="text-denim" />
          <Logo size={20} />
        </div>
        <h1 className="font-heading text-3xl font-bold mt-3">Admin</h1>
        <p className="text-sm text-timber-700">Beta access control</p>
      </header>

      <div className="px-5 max-w-md mx-auto w-full">
        <div className="flex gap-1.5 mb-3" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-full text-sm font-bold border-2 ${
                tab === t.key
                  ? "bg-timber-900 border-timber-900 text-masking"
                  : "bg-chalk border-stone_border text-timber-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <main className="space-y-2">
          {tab === "insights" ? (
            <InsightsPanel />
          ) : loading ? (
            <p className="text-sm text-timber-500">Loading…</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-timber-500" data-testid="admin-empty">No {tab} users.</p>
          ) : users.map((u) => (
            <div key={u.id} className="tactile-card p-3" data-testid={`admin-user-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => openDetail(u)}
                  className="min-w-0 flex-1 text-left group"
                  data-testid={`admin-user-row-${u.id}`}
                >
                  <p className="font-bold text-timber-900 truncate flex items-center gap-1">
                    {u.email}
                    <CaretRight size={14} weight="bold" className="text-timber-500 opacity-0 group-hover:opacity-100 transition" />
                  </p>
                  {u.name && <p className="text-xs text-timber-700">{u.name}</p>}
                  {u.reason && (
                    <p className="text-xs text-timber-700 italic mt-1 leading-snug">{`"${u.reason}"`}</p>
                  )}
                  <p className="text-[10px] text-timber-500 font-mono mt-1">
                    {u.is_admin && "ADMIN · "}
                    {u.requested_at && `req ${new Date(u.requested_at).toLocaleDateString()}`}
                    {u.approved_at && ` · approved ${new Date(u.approved_at).toLocaleDateString()}`}
                  </p>
                </button>
                {!u.is_admin && (
                  <div className="flex flex-col gap-1">
                    {tab === "pending" && (
                      <>
                        <Button
                          data-testid={`approve-${u.id}`}
                          onClick={() => approve(u.id)}
                          className="bg-ok text-white hover:bg-ok min-h-[36px] text-xs font-bold px-3"
                        >
                          <Check size={14} weight="bold" />
                          Approve
                        </Button>
                        <Button
                          data-testid={`reject-${u.id}`}
                          onClick={() => reject(u.id)}
                          variant="outline"
                          className="border-2 border-block text-block min-h-[36px] text-xs font-bold px-3"
                        >
                          <X size={14} weight="bold" />
                          Reject
                        </Button>
                      </>
                    )}
                    {tab === "approved" && (
                      <>
                        <Button
                          data-testid={`issue-code-${u.id}`}
                          onClick={() => issueCode(u)}
                          disabled={issuing === u.id}
                          className="bg-denim text-chalk hover:bg-denim-600 min-h-[36px] text-xs font-bold px-3"
                        >
                          <Key size={14} weight="bold" />
                          {issuing === u.id ? "…" : "Login code"}
                        </Button>
                        <Button
                          data-testid={`promote-${u.id}`}
                          onClick={() => askConfirm("promote", u)}
                          variant="outline"
                          className="border-2 border-denim text-denim min-h-[36px] text-xs font-bold px-3"
                        >
                          <ShieldCheck size={14} weight="bold" />
                          Make admin
                        </Button>
                        <Button
                          data-testid={`revoke-${u.id}`}
                          onClick={() => askConfirm("revoke", u)}
                          variant="outline"
                          className="border-2 border-block text-block min-h-[36px] text-xs font-bold px-3"
                        >
                          Revoke
                        </Button>
                      </>
                    )}
                    {tab === "rejected" && (
                      <Button
                        data-testid={`reapprove-${u.id}`}
                        onClick={() => approve(u.id)}
                        className="bg-ok text-white min-h-[36px] text-xs font-bold px-3"
                      >
                        Approve
                      </Button>
                    )}
                  </div>
                )}
                {u.is_admin && tab === "approved" && (
                  <div className="flex flex-col gap-1">
                    <Button
                      data-testid={`demote-${u.id}`}
                      onClick={() => askConfirm("demote", u)}
                      variant="outline"
                      className="border-2 border-block text-block min-h-[36px] text-xs font-bold px-3"
                    >
                      <ShieldCheck size={14} weight="bold" />
                      Remove admin
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </main>

        {/* Lightweight discovery dashboard — what users wish Half Job could do.
            Read-only counts + latest 20 submissions. No charts, no export. */}
        <FutureRequestsCard />
      </div>

      {/* Login Code modal */}
      <Dialog open={!!codeModal} onOpenChange={(o) => { if (!o) setCodeModal(null); }}>
        <DialogContent
          data-testid="login-code-modal"
          className="bg-chalk border-2 border-stone_border max-w-sm"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-timber-900 flex items-center gap-2">
              <Key size={22} weight="duotone" className="text-denim" /> Login code
            </DialogTitle>
            <DialogDescription className="text-timber-700 text-sm">
              Send this 6-digit code to{" "}
              <span className="font-bold text-timber-900">{codeModal?.user_email}</span>{" "}
              by SMS, Signal, or in person. It expires in {codeModal?.expires_in_minutes} minutes
              and can be used once.
            </DialogDescription>
          </DialogHeader>
          {codeModal && (
            <div className="py-2">
              <div className="bg-kraft border-2 border-dashed border-masking rounded-xl p-5 text-center">
                <div
                  data-testid="login-code-value"
                  className="font-heading text-4xl font-bold tracking-[0.4em] text-timber-900"
                >
                  {codeModal.code}
                </div>
                <p className="text-[11px] text-timber-500 mt-2 font-mono">
                  Expires in {codeModal.expires_in_minutes} min · single-use
                </p>
              </div>

              {/* Instructions block — admin can read these to the user, or copy
                  them with one tap and paste into SMS/Signal/email. The login
                  link is also copy-able separately for direct sharing. */}
              <div className="mt-4 bg-chalk border-2 border-stone_border rounded-xl p-3" data-testid="login-code-instructions">
                <p className="label-cap mb-2">Tell the user</p>
                <ol className="text-sm text-timber-900 space-y-1 list-decimal pl-5">
                  <li>{`Visit `}<span className="font-mono font-bold">{loginHost()}</span></li>
                  <li>{`Tap "Have a code already?"`}</li>
                  <li>{`Enter their email and the code above`}</li>
                </ol>
              </div>

              <Button
                data-testid="login-code-copy-btn"
                onClick={() => copy(codeModal.code)}
                className="w-full mt-4 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[52px] font-bold"
              >
                <Copy size={18} weight="bold" className="mr-2" /> Copy code
              </Button>
              <Button
                data-testid="login-code-copy-instructions-btn"
                onClick={() => copyInstructions(codeModal)}
                variant="outline"
                className="w-full mt-2 border-2 border-stone_border min-h-[52px] font-bold"
              >
                <Copy size={18} weight="bold" className="mr-2" /> Copy instructions + code
              </Button>
              <Button
                data-testid="login-code-copy-link-btn"
                onClick={() => copy(`${loginHost()}/code?email=${encodeURIComponent(codeModal.user_email)}`)}
                variant="outline"
                className="w-full mt-2 border-2 border-stone_border min-h-[52px] font-bold"
              >
                <Copy size={18} weight="bold" className="mr-2" /> Copy login link
              </Button>
              <a
                data-testid="login-code-email-btn"
                href={`mailto:${codeModal.user_email}?subject=${encodeURIComponent("Your Half Job sign-in code")}&body=${encodeURIComponent(adminCodeEmailBody(codeModal))}`}
                className="block text-center w-full mt-2 bg-chalk border-2 border-stone_border text-timber-900 hover:bg-kraft min-h-[52px] font-bold rounded-md py-3"
              >
                Compose email to {codeModal.user_email}
              </a>
              <p className="text-[11px] text-timber-500 mt-3 text-center">
                {`This code is shown only once. Issue a new one if it's lost.`}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Shared destructive-action confirm dialog (replaces window.confirm) */}
      <AlertDialog
        open={!!pendingConfirm}
        onOpenChange={(o) => { if (!o) setPendingConfirm(null); }}
      >
        <AlertDialogContent
          data-testid="admin-confirm-dialog"
          className="bg-chalk border-2 border-stone_border"
        >
          {pendingConfirm && (() => {
            const copy = CONFIRM_COPY[pendingConfirm.action] || {};
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle data-testid="admin-confirm-title">{copy.title}</AlertDialogTitle>
                  <AlertDialogDescription>
                    <span className="block font-bold text-timber-900">{pendingConfirm.user?.email}</span>
                    <span className="block text-timber-700 mt-1">{copy.description}</span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="admin-confirm-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="admin-confirm-confirm"
                    onClick={runConfirm}
                    className={
                      copy.destructive
                        ? "bg-block text-white hover:bg-block/90"
                        : "bg-denim text-chalk hover:bg-denim-600"
                    }
                  >
                    {copy.cta}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin User Detail drawer — open by tapping a user row. Houses the
          destructive Delete + Export JSON actions, plus quick stats. */}
      <Sheet open={!!detailUser} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <SheetContent
          side="right"
          data-testid="admin-user-detail"
          className="bg-chalk border-l-2 border-stone_border w-full sm:max-w-md overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle className="font-heading text-2xl text-timber-900">
              User detail
            </SheetTitle>
            <SheetDescription className="text-timber-700 text-sm">
              Inspect everything tied to this user. Export or delete from here.
            </SheetDescription>
          </SheetHeader>
          {detailUser && (
            <div className="mt-5 space-y-5">
              <div data-testid="admin-user-detail-header">
                <p className="font-bold text-timber-900 break-all">{detailUser.email}</p>
                {detailUser.name && <p className="text-sm text-timber-700">{detailUser.name}</p>}
                <p className="text-[11px] font-mono text-timber-500 mt-1">
                  {detailUser.is_admin && "ADMIN · "}
                  {detailUser.access_status?.toUpperCase()}
                  {detailUser.id === me?.id && " · YOU"}
                </p>
              </div>

              {detailLoading ? (
                <p className="text-sm text-timber-500">Loading stats…</p>
              ) : detail ? (
                <>
                  <div className="grid grid-cols-2 gap-2" data-testid="admin-user-stats">
                    <Stat label="Households" value={detail.stats.households} testid="stat-households" />
                    <Stat label="Owned" value={detail.stats.households_owned} testid="stat-owned" />
                    <Stat label="Projects (in households)" value={detail.stats.projects_in_households} testid="stat-projects" />
                    <Stat label="Future requests" value={detail.stats.future_requests} testid="stat-future-requests" />
                    <Stat label="Safety events" value={detail.stats.safety_events} testid="stat-safety-events" />
                    <Stat label="AI calls (24h)" value={detail.stats.ai_calls_24h} testid="stat-ai-calls" />
                  </div>

                  {detail.households.length > 0 && (
                    <div data-testid="admin-user-households">
                      <p className="label-cap mb-1">Households</p>
                      <div className="space-y-1.5">
                        {detail.households.map((h) => (
                          <div
                            key={h.id}
                            className="bg-kraft border border-stone_border rounded-lg p-2 text-sm"
                            data-testid={`admin-user-household-${h.id}`}
                          >
                            <p className="font-bold text-timber-900">{h.name || h.id}</p>
                            <p className="text-[11px] text-timber-700 font-mono">
                              {h.role?.toUpperCase()} · joined {h.joined_at ? new Date(h.joined_at).toLocaleDateString() : "—"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detail.user.beta_notes && (
                    <div>
                      <p className="label-cap mb-1">Beta notes</p>
                      <p className="text-sm text-timber-900 bg-kraft border border-stone_border rounded-lg p-2 whitespace-pre-line">
                        {detail.user.beta_notes}
                      </p>
                    </div>
                  )}

                  {/* User drill-down — projects, top categories, AI footprint. */}
                  {detail.drilldown && (
                    <div data-testid="admin-user-drilldown" className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Stat label="Projects" value={detail.drilldown.completion?.total} testid="drill-projects" />
                        <Stat label="In progress" value={detail.drilldown.completion?.in_progress} testid="drill-in-progress" />
                        <Stat label="Completed" value={detail.drilldown.completion?.completed} testid="drill-completed" />
                      </div>
                      {detail.drilldown.last_activity && (
                        <p className="text-[11px] text-timber-500 font-mono">
                          Last activity {new Date(detail.drilldown.last_activity).toLocaleString()}
                        </p>
                      )}
                      {detail.drilldown.top_categories?.length > 0 && (
                        <div data-testid="drill-categories">
                          <p className="label-cap mb-1">Top categories</p>
                          <ul className="space-y-0.5 text-sm">
                            {detail.drilldown.top_categories.slice(0, 5).map((c) => (
                              <li key={c.category} className="flex items-center justify-between">
                                <span className="text-timber-900">{c.category}</span>
                                <span className="font-mono font-bold text-timber-700">{c.count}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {detail.drilldown.projects?.length > 0 && (
                        <div data-testid="drill-projects-list">
                          <p className="label-cap mb-1">Recent projects</p>
                          <ul className="space-y-1.5">
                            {detail.drilldown.projects.slice(0, 6).map((p) => (
                              <li
                                key={p.id}
                                data-testid={`drill-project-${p.id}`}
                                className="bg-kraft border border-stone_border rounded-lg p-2 text-sm"
                              >
                                <p className="font-bold text-timber-900 truncate">{p.title}</p>
                                <p className="text-[11px] text-timber-700 font-mono">
                                  {p.category || "Uncategorised"} · {p.progress_pct}% · {p.status}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-[11px] text-timber-500 font-mono">
                        Terms accepted: {detail.drilldown.terms_accepted ? "Yes" : "No"}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2 pt-2 border-t border-stone_border">
                    <Button
                      data-testid="admin-user-export-btn"
                      onClick={() => exportUser(detailUser)}
                      variant="outline"
                      className="w-full border-2 border-denim text-denim min-h-[52px] font-bold"
                    >
                      <DownloadSimple size={16} weight="bold" className="mr-2" />
                      Export user data (JSON)
                    </Button>
                    <Button
                      data-testid="admin-user-delete-btn"
                      onClick={() => askConfirm("delete", detailUser)}
                      disabled={purging || detailUser.id === me?.id}
                      className="w-full bg-block text-white hover:bg-block/90 min-h-[52px] font-bold disabled:opacity-50"
                    >
                      <Trash size={16} weight="bold" className="mr-2" />
                      {detailUser.id === me?.id ? "Can't delete yourself" : (purging ? "Deleting…" : "Delete & purge user")}
                    </Button>
                    <p className="text-[11px] text-timber-500 text-center pt-1 leading-snug">
                      Deletion cascades through solo households, projects, photos and OTPs.
                      Shared households are preserved and ownership transfers to the next member.
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-block">{`Couldn't load detail.`}</p>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Small stat tile used in the admin user detail drawer.
function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-kraft border border-stone_border rounded-lg p-2"
    >
      <p className="text-[10px] uppercase tracking-wider font-bold text-timber-700">{label}</p>
      <p className="font-heading text-2xl font-bold text-timber-900">{value ?? 0}</p>
    </div>
  );
}

// -- Future requests insights card -------------------------------------------
// Polls `/api/admin/future-requests` once on mount. Renders four counts and a
// list of the latest 20 submissions. No charts, no export — discovery only.
const TYPE_LABELS = {
  home: "Home Job",
  personal: "Personal Project",
  work: "Work Project",
  hobby: "Hobby Project",
};

const TYPE_COLORS = {
  home: "bg-ok/15 text-ok border-ok",
  personal: "bg-denim/15 text-denim border-denim",
  work: "bg-masking/15 text-masking border-masking",
  hobby: "bg-timber-700/15 text-timber-900 border-timber-700",
};

function FutureRequestsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/future-requests")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData({ counts: {}, items: [] }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <section className="mt-8" data-testid="future-requests-card-loading">
        <p className="label-cap mb-2">Future requests</p>
        <p className="text-sm text-timber-500">Loading…</p>
      </section>
    );
  }

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <section className="mt-8" data-testid="future-requests-card">
      <div className="flex items-center gap-2 mb-2">
        <ChartBar size={18} weight="duotone" className="text-denim" />
        <p className="label-cap">Future requests</p>
      </div>
      <p className="text-xs text-timber-700 mb-3">
        What users wish Half Job could help them finish.
      </p>

      {/* Per-type counts grid */}
      <div className="grid grid-cols-2 gap-2" data-testid="future-requests-counts">
        {["home", "personal", "work", "hobby"].map((k) => (
          <div
            key={k}
            data-testid={`future-count-${k}`}
            className={`rounded-xl border-2 p-3 ${TYPE_COLORS[k]} bg-chalk`}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
              {TYPE_LABELS[k]}
            </p>
            <p className="font-heading text-3xl font-bold mt-0.5">{counts[k] || 0}</p>
          </div>
        ))}
      </div>

      {/* Latest 20 list */}
      <div className="mt-4 space-y-2" data-testid="future-requests-list">
        {items.length === 0 ? (
          <p className="text-sm text-timber-500 px-1">No requests yet.</p>
        ) : items.map((item) => (
          <div
            key={item.id}
            data-testid={`future-request-${item.id}`}
            className="tactile-card p-3"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border-2 ${TYPE_COLORS[item.selected_project_type]}`}>
                {TYPE_LABELS[item.selected_project_type] || item.selected_project_type}
              </span>
              <span className="text-[11px] text-timber-500">
                {item.timestamp ? new Date(item.timestamp).toLocaleString() : ""}
              </span>
            </div>
            {item.title && (
              <p className="font-bold text-timber-900 text-sm mt-1.5">{item.title}</p>
            )}
            <p className="text-sm text-timber-700 mt-1 leading-snug whitespace-pre-line">{item.feedback}</p>
            {item.user?.email && (
              <p className="text-[11px] text-timber-500 mt-1.5">
                from <span className="font-bold">{item.user.email}</span>
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
